import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Card,
    CircularProgress,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import { AccountBalanceWalletRounded } from "@mui/icons-material";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Report_corporateBudgetByMonth } from "../../../network/api/report.api";
import { toPriceWithComma } from "../../../helper/utils";

const _DashBudgetTrendChart = ({ corporateId }) => {
    const theme = useTheme();
    const [raw, setRaw] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!corporateId) return;
        setLoading(true);
        Report_corporateBudgetByMonth(corporateId)
            .then((res) => {
                setRaw(res?.data?.Data || null);
            })
            .catch(() => setRaw(null))
            .finally(() => setLoading(false));
    }, [corporateId]);

    // ادغام نقاط شارژ کیف و خرج‌شده در یک آرایه برای Recharts
    const chartData = useMemo(() => {
        if (!raw?.Series || raw.Series.length === 0) return [];

        const chargedSeries = raw.Series.find(s => s.Key === "CHARGED")?.Points || [];
        const spentSeries = raw.Series.find(s => s.Key === "SPENT")?.Points || [];

        const labelSet = new Set([
            ...chargedSeries.map(p => p.Label),
            ...spentSeries.map(p => p.Label),
        ]);

        const chargedMap = new Map(chargedSeries.map(p => [p.Label, p.Value || 0]));
        const spentMap = new Map(spentSeries.map(p => [p.Label, p.Value || 0]));

        return Array.from(labelSet).map(label => ({
            label,
            charged: chargedMap.get(label) || 0,
            spent: spentMap.get(label) || 0,
        }));
    }, [raw]);

    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                        direction: 'rtl',
                        textAlign: 'right',
                    }}
                >
                    <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 1, color: 'text.secondary' }}>
                        دوره: {label}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                {entry.name}:
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                {toPriceWithComma(entry.value)} تومان
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );
        }
        return null;
    };

    return (
        <Card
            elevation={0}
            sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: 2.5,
                            bgcolor: 'rgba(45, 107, 226, 0.1)',
                            color: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <AccountBalanceWalletRounded sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                            روند بودجه و هزینه‌کرد ۱۲ ماهه
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            مقایسه مبالغ شارژ کیف پول و خرج‌کرد پرسنل (تومان)
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ width: '100%', height: 320, mt: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <CircularProgress size={32} />
                    </Box>
                ) : chartData.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            داده‌ای برای روند بودجه ثبت نشده است.
                        </Typography>
                    </Box>
                ) : (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                            <defs>
                                <linearGradient id="chargedGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.25} />
                                    <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                                </linearGradient>
                                <linearGradient id="spentGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#ed6c02" stopOpacity={0.25} />
                                    <stop offset="95%" stopColor="#ed6c02" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                            <XAxis
                                dataKey="label"
                                stroke={theme.palette.text.secondary}
                                fontSize={11}
                                tickLine={false}
                            />
                            <YAxis
                                stroke={theme.palette.text.secondary}
                                fontSize={11}
                                tickLine={false}
                                tickFormatter={(val) => val >= 1000000 ? `${val / 1000000}M` : val}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                wrapperStyle={{ fontSize: '0.8rem', paddingTop: '10px' }}
                                formatter={(value) => (value === 'charged' ? 'شارژ کیف پول' : 'خرج‌شده')}
                            />
                            <Area
                                type="monotone"
                                dataKey="charged"
                                name="charged"
                                stroke={theme.palette.primary.main}
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#chargedGrad)"
                            />
                            <Area
                                type="monotone"
                                dataKey="spent"
                                name="spent"
                                stroke="#ed6c02"
                                strokeWidth={2.5}
                                fillOpacity={1}
                                fill="url(#spentGrad)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                )}
            </Box>
        </Card>
    );
};

export default _DashBudgetTrendChart;
